import * as React from 'react';
import { Box, Container, Typography, Grid, Link, styled } from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PhoneInTalkOutlineIcon from '@mui/icons-material/PhoneInTalkOutlined';
import { LinkedIn } from '@mui/icons-material';

const PatternOverlay = styled(Box)({
  position: 'absolute',
  inset: 0,
  backgroundImage: "url(\"data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMiI+PHBhdGggZD0iTTM2IDM0djItaDJ2LTJoLTJ6bTAtNHYyaDJ2LTJoLTJ6bTAtNHYyaDJ2LTJoLTJ6bTAtNHYyaDJ2LTJoLTJ6bTAtNHYyaDJ2LTJoLTJ6bTItNnYyaDJ2LTJoLTJ6bTAtNHYyaDJ2LTJoLTJ6bTAtNHYyaDJ2LTJoLTJ6bTAtNHYyaDJ2LTJoLTJ6Ii8+PC9nPjwvZz48L3N2Z34=\")",
  opacity: 0.4,
  zIndex: 0,
});

export default function Footer({ siteSettings = {} }) {
  const s = siteSettings;

  const customColors = {
    'cyan100': '#CFFAFE',
    'cyan200': '#A5F3FC',
    'cyan300': '#67E8F9',
    'cyan400': '#22D3EE',
    'blue100': '#DBEAFE',
    'blue400': '#60A5FA',
  };

  const gradientBackground = 'linear-gradient(to bottom right, #0B3D6B, #0F4C7A, #1565A0)';
  const hoverTextColor = customColors.cyan300;

  const LinkItem = ({ href, text, target = '_self', rel = '' }) => (
    <Box component="li">
      <Link
        href={href}
        target={target}
        rel={rel}
        color="inherit"
        underline="none"
        sx={{
          color: customColors.blue100,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1,
          transition: 'color 0.3s',
          position: 'relative',
          '&:hover': {
            color: hoverTextColor,
            '& .dot-transition': {
              width: 8,
              mr: 0.5,
            }
          }
        }}
      >
        <Box
          className="dot-transition"
          sx={{
            width: 4,
            height: 4,
            backgroundColor: customColors.cyan400,
            borderRadius: '50%',
            transition: 'all 0.3s',
            mr: 1,
          }}
        />
        {text}
      </Link>
    </Box>
  );

  return (
    <Box
      component="footer"
      sx={{
        background: gradientBackground,
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <PatternOverlay />

      <Container maxWidth="xl" sx={{ px: 3, py: { xs: 4, md: 6 }, position: 'relative', zIndex: 10 }}>

        <Grid container spacing={{ xs: 6, md: 12, lg: 4 }} sx={{ mb: { xs: 8, md: 12 } }}>

          <Grid item xs={12} md={6} lg={3}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: 'white' }}>
                NC Pro
              </Typography>
              <Typography sx={{ color: customColors.cyan100, fontSize: '0.875rem' }}>
                AI-Powered Non-Conformity Management Tool
              </Typography>
            </Box>
            <Typography sx={{ color: customColors.blue100, mb: 3, lineHeight: 1.6 }}>
              Streamline Quality. Eliminate Errors. Empower Teams.
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '0.875rem', color: customColors.cyan200 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  backgroundColor: customColors.cyan400,
                  borderRadius: '50%',
                  animation: 'pulse 2s infinite cubic-bezier(0.4, 0, 0.6, 1)',
                  '@keyframes pulse': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0.5 },
                  },
                }}
              />
              <span>Powered by AI & 8D Methodology</span>
            </Box>
          </Grid>

          <Grid item xs={12} md={6} lg={3}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: customColors.cyan100 }}>Quick Links</Typography>
            <Box component="ul" sx={{ listStyle: 'none', p: 0, mt: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <LinkItem href="/#features" text="Features" />
              <LinkItem href="/#benefits" text="Benefits" />
              <LinkItem href="/#demo" text="Request Demo" />
              <LinkItem href="/article" text="Article" />
              <LinkItem href="https://www.ncpro.gbinternational.sg" text="Visit Website" target="_blank" rel="noopener noreferrer" />
            </Box>
          </Grid>

          <Grid item xs={12} md={6} lg={3}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: customColors.cyan100 }}>Contact Us</Typography>
            <Box component="ul" sx={{ listStyle: 'none', p: 0, mt: 0.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box component="li">
                <Link
                  href={`mailto:${s.contact_email || 'admin@gbinternational.sg'}`}
                  color="inherit"
                  underline="none"
                  sx={{
                    color: customColors.blue100,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5,
                    transition: 'color 0.3s',
                    '&:hover': { color: hoverTextColor, '& .MuiSvgIcon-root': { transform: 'scale(1.1)' } }
                  }}
                >
                  <MailOutlineIcon sx={{ width: 20, height: 20, flexShrink: 0, mt: 0.5, transition: 'transform 0.3s' }} />
                  <Typography component="span" sx={{ wordBreak: 'break-all', fontSize: '1rem' }}>{s.contact_email || 'admin@gbinternational.sg'}</Typography>
                </Link>
              </Box>
              <Box component="li">
                <Link
                  href={`tel:${(s.contact_phone || '+91-9940159181').replace(/[^+\d]/g, '')}`}
                  color="inherit"
                  underline="none"
                  sx={{
                    color: customColors.blue100,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5,
                    transition: 'color 0.3s',
                    '&:hover': { color: hoverTextColor, '& .MuiSvgIcon-root': { transform: 'scale(1.1)' } }
                  }}
                >
                  <PhoneInTalkOutlineIcon sx={{ width: 20, height: 20, flexShrink: 0, mt: 0.5, transition: 'transform 0.3s' }} />
                  <Typography component="span" sx={{ fontSize: '1rem' }}>{s.contact_phone || '+91-9940159181'}</Typography>
                </Link>
              </Box>
              <Box component="li">
                <Link
                  href="https://www.ncpro.gbinternational.sg"
                  target="_blank"
                  rel="noopener noreferrer"
                  color="inherit"
                  underline="none"
                  sx={{
                    color: customColors.blue100,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5,
                    transition: 'color 0.3s',
                    '&:hover': { color: hoverTextColor, '& .MuiSvgIcon-root': { transform: 'scale(1.1)' } }
                  }}
                >
                  <LanguageIcon sx={{ width: 20, height: 20, flexShrink: 0, mt: 0.5, transition: 'transform 0.3s' }} />
                  <Typography component="span" sx={{ wordBreak: 'break-all', fontSize: '1rem' }}>www.ncpro.gbinternational.sg</Typography>
                </Link>
              </Box>
              <Box component="li">
                <Link
                  href="https://in.linkedin.com/company/gb-international-sourcing-engineering-pte-ltd"
                  target="_blank"
                  rel="noopener noreferrer"
                  color="inherit"
                  underline="none"
                  sx={{
                    color: customColors.blue100,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5,
                    transition: 'color 0.3s',
                    '&:hover': { color: hoverTextColor, '& .MuiSvgIcon-root': { transform: 'scale(1.1)' } }
                  }}
                >
                  <LinkedIn sx={{ width: 20, height: 20, flexShrink: 0, mt: 0.5, transition: 'transform 0.3s' }} />
                  <Typography component="span" sx={{ wordBreak: 'break-all', fontSize: '1rem' }}>linkedin</Typography>
                </Link>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={6} lg={3}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: customColors.cyan100 }}>Company Info</Typography>
            <Box sx={{ color: customColors.blue100, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'white', mb: 0.5 }}>{s.company_name || 'GB International Sourcing & Engg Pte Ltd'}</Typography>
                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>{s.company_location || 'Singapore'}</Typography>
                <Typography variant="body2" sx={{ fontSize: '0.875rem', color: customColors.cyan200 }}>UEN: {s.company_uen || '202413293G'}</Typography>
              </Box>
              <Box sx={{ pt: 2, borderTop: `1px solid rgba(255, 255, 255, 0.2)` }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'white', mb: 0.5 }}>Seller Info</Typography>
                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>{s.seller_name || 'GEBI SOURCING (INDIA) PVT LTD'}</Typography>
                <Typography variant="body2" sx={{ fontSize: '0.875rem', color: customColors.cyan200 }}>GSTIN: {s.seller_gstin || '33AMZPA3155B1ZC'}</Typography>
              </Box>
            </Box>
          </Grid>

        </Grid>

        <Box sx={{ borderTop: `1px solid rgba(255, 255, 255, 0.2)`, pt: 4 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', md: 'center' },
              gap: 2,
            }}
          >
            <Typography sx={{ color: customColors.blue100, fontSize: '0.875rem', order: { xs: 2, md: 1 } }}>
              Copyright © GB International Sourcing & Engineering Pte Ltd. All rights reserved.
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: '0.875rem', color: customColors.blue100, order: { xs: 1, md: 2 } }}>
              <Link href="/images/docs/privacy-policy.pdf" underline="none" sx={{ color: 'inherit', transition: 'color 0.3s', '&:hover': { color: hoverTextColor } }}>
                Privacy Policy
              </Link>

              <Link href="/images/docs/terms-of-use.pdf" underline="none" sx={{ color: 'inherit', transition: 'color 0.3s', '&:hover': { color: hoverTextColor } }}>
                Terms of Service
              </Link>
            </Box>
          </Box>
        </Box>
      </Container>

      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: 384,
          height: 384,
          backgroundColor: customColors.cyan400,
          borderRadius: '50%',
          filter: 'blur(48px)',
          opacity: 0.1,
          transform: 'translate(-50%, 50%)',
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: 384,
          height: 384,
          backgroundColor: customColors.blue400,
          borderRadius: '50%',
          filter: 'blur(48px)',
          opacity: 0.1,
          transform: 'translate(50%, -50%)',
          zIndex: 0,
        }}
      />
    </Box>
  );
}
